// data-access-rls §7, §7.1: "מה הוא רואה" במגירת המשתמש (admin ובעל האצלה) ופס "מוצגים רק הנתונים שהוקצו לך" למשתמש מוגבל.
// §7.1 חוויית המאציל: בעלים לא טכני בטלפון בין פגישות. "רואה הכול" / "רק חלק" → לכל חלוקה בעץ (catalog.dimensions, בשם שלה)
// בחירה מהרשימה החיה לפי שם → אפשר להסתיר סוגי נתונים ופרטים → משפט אישור עם מספרים אמיתיים (preview-draft) → "שמור";
// היסטוריה עם "בטל שינוי". גנרי לגמרי: כל שם על המסך מגיע מהעץ של המופע (nameHe של סוג נתונים, פרט, חלוקה, ושם הערך).
// הלקוח רק בונה את הבחירה ומציג; התקרה (exceeds_grantor), admin שלא ניתן להגבלה ו-RLS נאכפים בשרת, והודעת השרת מוצגת כמו שהיא.
import { useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { usersApi, type DaBody, type DaCatalog, type DaDimension, type DaDraftPreview, type DaView } from '../lib/usersApi.ts';
import { ErrorView, Loading } from './Status.tsx';
import { Button } from './ui/button.tsx';
import { Badge } from './ui/badge.tsx';
import { Input, Select } from './ui/input.tsx';
import { IconCheck, IconChevronForward, IconHistory, IconLock, IconSearch, IconSliders, IconUsers } from './brand/icons.tsx';

const D = T.users.da;
const k2 = (world: string, entity: string) => `${world}.${entity}`;
const ALL: DaBody = { mode: 'all', rows: [], denyEntities: [], denyColumns: [] };
const bodyOf = (v: DaBody): DaBody => ({ mode: v.mode, rows: v.rows, denyEntities: v.denyEntities, denyColumns: v.denyColumns });
const and = (xs: string[]) => (xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} ו${xs.at(-1)}`);

/** הבחירה בזמן עריכה: ערכים לכל חלוקה, סוגי נתונים מוסתרים, פרטים מוסתרים לכל סוג. */
type Pick = { values: Record<string, string[]>; hideE: string[]; hideC: Record<string, string[]> };

function toPick(b: DaBody, cat: DaCatalog): Pick {
  const values: Record<string, string[]> = {};
  for (const d of cat.dimensions) values[d.id] = [...new Set(b.rows.filter((r) => r.world === d.world && d.entities.some((e) => e.entity === r.entity)).flatMap((r) => r.values))];
  const hideC: Record<string, string[]> = {};
  for (const c of b.denyColumns) (hideC[k2(c.world, c.entity)] ??= []).push(c.column);
  return { values, hideE: b.denyEntities.map((e) => k2(e.world, e.entity)), hideC };
}

function toBody(p: Pick, cat: DaCatalog): DaBody {
  const hidden = new Set(p.hideE);
  const ents = cat.entities.filter((e) => !hidden.has(k2(e.world, e.entity)));
  return {
    mode: 'restricted',
    rows: cat.dimensions.flatMap((d) => {
      const values = [...(p.values[d.id] ?? [])].sort();
      return values.length ? d.entities.filter((e) => !hidden.has(k2(d.world, e.entity))).map((e) => ({ world: d.world, entity: e.entity, values })) : [];
    }),
    denyEntities: cat.entities.filter((e) => hidden.has(k2(e.world, e.entity))).map((e) => ({ world: e.world, entity: e.entity })),
    denyColumns: ents.flatMap((e) => (p.hideC[k2(e.world, e.entity)] ?? []).sort().map((column) => ({ world: e.world, entity: e.entity, column }))),
  };
}

type Step = { s: 'now' } | { s: 'mode' } | { s: 'pick'; pick: Pick } | { s: 'confirm'; body: DaBody; pick?: Pick };

/**
 * הלשונית במגירה. בעל האצלה בלי הרשאה על היעד (editable=false) לא רואה אותה כלל; admin רואה אותה גם כשאי אפשר לשנות.
 * `guided` (מיד אחרי "משתמש חדש"): נפתח ישר ב-"מה הוא יראה?".
 */
export function DataAccessSection({ userId, isAdmin, users, guided = false }: { userId: string; isAdmin: boolean; users: Array<{ id: string; nameHe: string }>; guided?: boolean }) {
  const q = useQuery({ queryKey: ['user-data-access', userId], queryFn: () => usersApi.dataAccess(userId), retry: false, staleTime: 0 });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} />;
  if (q.data.editable === false && !isAdmin) return null;
  return <Flow key={userId} view={q.data} users={users.filter((u) => u.id !== userId)} guided={guided} />;
}

function Flow({ view, users, guided }: { view: DaView; users: Array<{ id: string; nameHe: string }>; guided: boolean }) {
  const qc = useQueryClient();
  const id = view.user.id;
  const name = view.user.nameHe;
  const canEdit = view.restrictable && view.editable !== false;
  const [step, setStep] = useState<Step>(guided && canEdit ? { s: 'mode' } : { s: 'now' });
  const [justSaved, setJustSaved] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const catalog = useQuery({ queryKey: ['da-catalog'], queryFn: usersApi.daCatalog, enabled: canEdit, retry: false, staleTime: 60_000 });

  const onSaved = (v: DaView) => {
    qc.setQueryData(['user-data-access', id], v);
    void qc.invalidateQueries({ queryKey: ['da-history', id] });
    void qc.invalidateQueries({ queryKey: ['da-preview', id] });
    void qc.invalidateQueries({ queryKey: ['users-log'] });
    void qc.invalidateQueries({ queryKey: ['artifact-share'] });
    setJustSaved(true);
    setStep({ s: 'now' });
  };
  const copyFrom = async (from: string) => {
    if (!from || !catalog.data) return;
    setErr(null);
    try {
      const b = await usersApi.daCopy(from);
      setStep(b.mode === 'all' ? { s: 'confirm', body: ALL } : { s: 'pick', pick: toPick(b, catalog.data) });
    } catch (e) {
      setErr(e);
    }
  };

  if (!view.restrictable)
    return (
      <p className="m-0 inline-flex items-center gap-1.5 text-sm text-muted-foreground" data-testid="da-admin-note">
        <IconLock size={14} />
        {D.adminNote}
      </p>
    );

  return (
    <div className="flex flex-col gap-3" data-testid="da-flow" data-step={step.s}>
      {view.asDelegate && canEdit && step.s !== 'now' && <p className="m-0 text-[13px] text-muted-foreground">{D.delegateNote}</p>}
      {step.s === 'now' && (
        <>
          <NowCard id={id} name={name} body={bodyOf(view)} justSaved={justSaved} canEdit={canEdit} onChange={() => (setJustSaved(false), setStep({ s: 'mode' }))} />
          <History id={id} onRestored={onSaved} />
        </>
      )}
      {step.s !== 'now' && catalog.isPending && <Loading variant="rows" />}
      {step.s !== 'now' && catalog.isError && <ErrorView error={catalog.error} />}
      {catalog.data && step.s === 'mode' && (
        <div className="flex flex-col gap-3" data-testid="da-mode">
          <h4 className="m-0 text-lg font-bold">{D.ask(name)}</h4>
          <BigChoice testId="da-mode-all" icon={<IconUsers size={22} />} title={D.all} sub={D.allSub} current={view.mode === 'all'} onClick={() => setStep({ s: 'confirm', body: ALL })} />
          <BigChoice
            testId="da-mode-some"
            icon={<IconSliders size={22} />}
            title={D.some}
            sub={D.someSub}
            current={view.mode === 'restricted'}
            onClick={() => setStep({ s: 'pick', pick: toPick(bodyOf(view), catalog.data) })}
          />
          <div className="flex flex-wrap items-center gap-2">
            {users.length > 0 && (
              <Select aria-label={D.copyFrom} className="w-fit" value="" onChange={(e) => void copyFrom(e.target.value)} data-testid="da-copy">
                <option value="">{D.copyFrom}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nameHe}
                  </option>
                ))}
              </Select>
            )}
            <Button variant="ghost" size="sm" onClick={() => setStep({ s: 'now' })} data-testid="da-back">
              {D.cancel}
            </Button>
          </div>
          {err != null && <ErrorView error={err} />}
        </div>
      )}
      {catalog.data && step.s === 'pick' && (
        <PickStep cat={catalog.data} initial={step.pick} onBack={() => setStep({ s: 'mode' })} onNext={(pick) => setStep({ s: 'confirm', body: toBody(pick, catalog.data), pick })} />
      )}
      {step.s === 'confirm' && (
        <Confirm id={id} name={name} body={step.body} onBack={() => setStep(step.pick ? { s: 'pick', pick: step.pick } : { s: 'mode' })} onSaved={onSaved} />
      )}
    </div>
  );
}

function BigChoice({ testId, icon, title, sub, current, onClick }: { testId: string; icon: ReactNode; title: string; sub: string; current: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-16 cursor-pointer items-center gap-3 rounded-md border bg-card p-3 text-start shadow-card transition-colors hover:border-primary/50 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring',
        current ? 'border-primary/50' : 'border-border',
      )}
      data-testid={testId}
      data-current={current ? '1' : '0'}
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-circle bg-primary-soft text-primary">{icon}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-base font-bold">{title}</span>
        <span className="text-sm text-muted-foreground">{sub}</span>
      </span>
      <IconChevronForward size={18} className="shrink-0 text-muted-foreground" />
    </button>
  );
}

/** "עכשיו הוא רואה: …" (המשפט מהשרת), ואחרי שמירה "נשמר. מעכשיו … רואה:". */
function NowCard({ id, name, body, justSaved, canEdit, onChange }: { id: string; name: string; body: DaBody; justSaved: boolean; canEdit: boolean; onChange: () => void }) {
  const p = useQuery({ queryKey: ['da-draft', id, JSON.stringify(body)], queryFn: () => usersApi.daPreviewDraft(body, id), retry: false, staleTime: 0 });
  const [detail, setDetail] = useState(false);
  return (
    <div className={cn('flex flex-col gap-2 rounded-md border p-3', justSaved ? 'border-ok/40 bg-ok/[0.06]' : 'border-border bg-card')} data-testid="da-now">
      <span
        className={cn('inline-flex items-center gap-1.5 text-[13px] font-semibold', justSaved ? 'text-ok' : 'text-muted-foreground')}
        {...(justSaved ? { role: 'status', 'data-testid': 'da-saved' } : {})}
      >
        {justSaved && <IconCheck size={15} />}
        {justSaved ? D.savedNow(name) : D.now}
      </span>
      {p.isPending && <Loading />}
      {p.isError && <ErrorView error={p.error} />}
      {p.data && (
        <p className="m-0 text-base font-semibold" data-testid="da-now-summary">
          {p.data.summaryHe}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {canEdit && (
          <Button variant="outline" onClick={onChange} data-testid="da-change">
            {D.change}
          </Button>
        )}
        {body.mode === 'restricted' && p.data && (
          <Button variant="ghost" size="sm" className="ms-auto" onClick={() => setDetail((v) => !v)} aria-expanded={detail} data-testid="da-detail-toggle">
            {detail ? D.detailHide : D.detail}
          </Button>
        )}
      </div>
      {detail && p.data && (
        <>
          <EntityList p={p.data} />
          <Spreads id={id} />
        </>
      )}
    </div>
  );
}

/** לכל חלוקה בעץ: בחירה מהרשימה החיה לפי שם; ואז (לא חובה) להסתיר סוגי נתונים ופרטים. */
function PickStep({ cat, initial, onBack, onNext }: { cat: DaCatalog; initial: Pick; onBack: () => void; onNext: (p: Pick) => void }) {
  const [p, setP] = useState<Pick>(initial);
  const [more, setMore] = useState(initial.hideE.length > 0 || Object.values(initial.hideC).some((x) => x.length));
  return (
    <div className="flex flex-col gap-4" data-testid="da-pick">
      {cat.dimensions.map((d) => (
        <DimPicker key={d.id} d={d} selected={p.values[d.id] ?? []} hidden={p.hideE} onChange={(values) => setP((x) => ({ ...x, values: { ...x.values, [d.id]: values } }))} />
      ))}
      {cat.entities.length > 0 && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="inline-flex min-h-11 w-fit cursor-pointer items-center gap-1.5 text-sm font-semibold text-primary"
            onClick={() => setMore((v) => !v)}
            aria-expanded={more}
            data-testid="da-more"
          >
            <IconChevronForward size={16} className={cn('transition-transform', more && 'rotate-90 rtl:-rotate-90')} />
            {D.more}
          </button>
          {more && (
            <ul className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0" data-testid="da-kinds">
              {cat.entities.map((e) => {
                const key = k2(e.world, e.entity);
                const off = p.hideE.includes(key);
                const cols = p.hideC[key] ?? [];
                return (
                  <li key={key} className="flex flex-col gap-2 px-3 py-2" data-testid={`da-kind-${key}`} data-hidden={off ? '1' : '0'}>
                    <label className="flex min-h-9 cursor-pointer items-center gap-3">
                      <span className={cn('min-w-0 flex-1 font-semibold', off && 'text-muted-foreground line-through')}>{e.nameHe}</span>
                      <span className="text-[13px] text-muted-foreground">{D.kindHidden}</span>
                      <input
                        type="checkbox"
                        role="switch"
                        className="size-5 shrink-0 accent-[hsl(var(--primary))]"
                        checked={off}
                        onChange={(ev) => setP((x) => ({ ...x, hideE: ev.target.checked ? [...x.hideE, key] : x.hideE.filter((k) => k !== key) }))}
                        data-testid="da-kind-off"
                      />
                    </label>
                    {!off && e.columns.length > 0 && (
                      <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
                        <legend className="mb-1 text-[13px] text-muted-foreground">{D.fieldsHidden}</legend>
                        <div className="flex flex-wrap gap-1.5">
                          {e.columns.map((c) => {
                            const on = cols.includes(c.key);
                            return (
                              <button
                                key={c.key}
                                type="button"
                                aria-pressed={on}
                                onClick={() => setP((x) => ({ ...x, hideC: { ...x.hideC, [key]: on ? cols.filter((y) => y !== c.key) : [...cols, c.key] } }))}
                                className={cn(
                                  'inline-flex min-h-9 items-center gap-1.5 rounded-sm px-3 text-sm ring-1 transition-colors ring-inset',
                                  on ? 'bg-primary-soft font-semibold text-primary ring-primary line-through' : 'bg-card ring-border hover:bg-muted',
                                )}
                                data-testid="da-field"
                                data-field={c.key}
                              >
                                {c.nameHe}
                              </button>
                            );
                          })}
                        </div>
                      </fieldset>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button onClick={() => onNext(p)} data-testid="da-next">
          {D.next}
        </Button>
        <Button variant="ghost" onClick={onBack} data-testid="da-back">
          {D.back}
        </Button>
      </div>
    </div>
  );
}

/** חלוקה אחת: הכותרת = השם שלה בעץ; רשימה עם חיפוש לפי שם התצוגה של הערך (הקוד רק כשאין שם). */
function DimPicker({ d, selected, hidden, onChange }: { d: DaDimension; selected: string[]; hidden: string[]; onChange: (v: string[]) => void }) {
  const first = d.entities[0];
  const q = useQuery({ queryKey: ['da-values', d.world, first?.entity], queryFn: () => usersApi.daValues(d.world, first!.entity), enabled: !!first, retry: false, staleTime: 60_000 });
  const [text, setText] = useState('');
  const needle = text.trim().toLowerCase();
  const list = (q.data?.values ?? []).filter((v) => !needle || `${v.label ?? ''} ${v.value}`.toLowerCase().includes(needle));
  const ents = and(d.entities.filter((e) => !hidden.includes(k2(d.world, e.entity))).map((e) => e.nameHe));
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((y) => y !== v) : [...selected, v]);
  return (
    <section className="flex flex-col gap-2" data-testid="da-dim" data-dim={d.id}>
      <div className="flex items-baseline gap-2">
        <h4 className="m-0 text-lg font-bold">{d.nameHe}</h4>
        {selected.length > 0 && <Badge tone="info">{D.nPicked(selected.length)}</Badge>}
      </div>
      {ents && <p className="m-0 text-[13px] text-muted-foreground">{D.pickHint(ents)}</p>}
      {(q.data?.values.length ?? 0) > 6 && (
        <div className="relative">
          <IconSearch size={17} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input type="search" className="ps-10" placeholder={D.pickSearch} aria-label={`${D.pickSearch}: ${d.nameHe}`} value={text} onChange={(e) => setText(e.target.value)} data-testid="da-pick-search" />
        </div>
      )}
      {q.isPending && <Loading variant="rows" />}
      {q.isError && <ErrorView error={q.error} />}
      {q.data && (
        <ul className="m-0 flex max-h-80 list-none flex-col divide-y divide-border overflow-y-auto rounded-md border border-border bg-card p-0" data-testid="da-pick-list">
          {list.length === 0 && <li className="p-3 text-sm text-muted-foreground">{D.pickNone}</li>}
          {list.map((v) => {
            const on = selected.includes(v.value);
            return (
              <li key={v.value}>
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggle(v.value)}
                  className={cn('flex min-h-12 w-full cursor-pointer items-center gap-3 px-3 py-2 text-start transition-colors', on ? 'bg-primary-soft' : 'hover:bg-muted')}
                  data-testid="da-pick-item"
                  data-value={v.value}
                >
                  <span className={cn('grid size-6 shrink-0 place-items-center rounded-sm ring-1 ring-inset', on ? 'bg-primary text-primary-foreground ring-primary' : 'ring-border')}>{on && <IconCheck size={15} />}</span>
                  <span className={cn('min-w-0 flex-1 truncate', v.label ? 'font-semibold' : 'text-sm text-muted-foreground')} dir="auto">
                    {v.label ?? v.value}
                  </span>
                  {first && <span className="shrink-0 text-[13px] text-muted-foreground tabular-nums">{D.rowsOf(v.rows, first.nameHe)}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {q.data?.truncated && <p className="m-0 text-[13px] text-muted-foreground">{D.truncated}</p>}
      {q.data && selected.length === 0 && ents && (
        <p className="m-0 text-[13px] font-semibold text-degraded" data-testid="da-pick-empty">
          {D.pickEmpty(ents)}
        </p>
      )}
    </section>
  );
}

/** "בשמירה X יראה: 48 …, 1,260 …" — מספרים אמיתיים לבחירה שעוד לא נשמרה (preview-draft); ואז "שמור". */
function Confirm({ id, name, body, onBack, onSaved }: { id: string; name: string; body: DaBody; onBack: () => void; onSaved: (v: DaView) => void }) {
  const key = useMemo(() => JSON.stringify(body), [body]);
  const p = useQuery({ queryKey: ['da-draft', id, key], queryFn: () => usersApi.daPreviewDraft(body, id), retry: false, staleTime: 0 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const save = async () => {
    setErr(null);
    setBusy(true);
    try {
      onSaved(await usersApi.setDataAccess(id, body));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-col gap-3" data-testid="da-confirm">
      <h4 className="m-0 text-lg font-bold">{D.confirmTitle(name)}</h4>
      <div className="flex flex-col gap-2 rounded-md border border-primary/30 bg-primary-soft/60 p-3" aria-live="polite">
        {p.isPending && <Loading />}
        {p.isError && <ErrorView error={p.error} />}
        {p.data && (
          <p className="m-0 text-lg font-bold leading-snug" data-testid="da-confirm-summary">
            {p.data.summaryHe}
          </p>
        )}
      </div>
      {p.data && body.mode === 'restricted' && <EntityList p={p.data} />}
      {err != null && <ErrorView error={err} />}
      <div className="flex items-center gap-2">
        <Button size="lg" disabled={busy || !p.data} onClick={() => void save()} data-testid="da-save">
          {busy ? D.saving : D.save}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onBack} data-testid="da-back">
          {D.back}
        </Button>
      </div>
    </div>
  );
}

/** לכל סוג נתונים: כמה יראה, ואילו פרטים לא. */
function EntityList({ p }: { p: DaDraftPreview }) {
  return (
    <ul className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0 text-sm" data-testid="da-entities">
      {p.entities.map((e) => (
        <li key={k2(e.world, e.entity)} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2" data-testid="da-entity" data-entity={k2(e.world, e.entity)} data-blocked={e.blocked ? '1' : '0'}>
          <span className="min-w-0 flex-1 font-medium">{e.nameHe}</span>
          <span className={cn('tabular-nums', e.blocked ? 'font-semibold text-error' : 'text-foreground')} data-testid="da-entity-rows">
            {e.blocked ? D.rowsNone : e.rows != null ? e.rows.toLocaleString('he-IL') : D.rowsAll}
          </span>
          {!e.blocked && e.hiddenColumns.length > 0 && (
            <span className="w-full text-[13px] text-muted-foreground">
              {D.fieldsHidden}: {e.hiddenColumns.join(', ')}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** "סיכומים מוכנים": זמין / לא זמין לו (אחרי שמירה, בזהותו). */
function Spreads({ id }: { id: string }) {
  const q = useQuery({ queryKey: ['da-preview', id], queryFn: () => usersApi.daPreview(id), retry: false, staleTime: 0 });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} />;
  if (!q.data.spreads.length) return null;
  return (
    <div className="flex flex-col gap-1.5" data-testid="da-spreads">
      <span className="text-[13px] font-semibold">{D.spreads}</span>
      <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
        {q.data.spreads.map((s) => (
          <li key={s.key} data-testid="da-spread" data-access={s.access}>
            <Badge tone={s.access === 'ok' ? 'ok' : 'error'} className="gap-1">
              {s.access !== 'ok' && <IconLock size={12} />}
              {s.nameHe}: {s.access === 'ok' ? D.spreadOk : D.spreadBlocked}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "מי שינה מה ומתי" — 10 האחרונים (3 גלויים); "בטל שינוי" מחזיר את המצב שלפני השינוי. */
function History({ id, onRestored }: { id: string; onRestored: (v: DaView) => void }) {
  const q = useQuery({ queryKey: ['da-history', id], queryFn: () => usersApi.daHistory(id), retry: false, staleTime: 0 });
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const undo = async (changeId: string) => {
    setErr(null);
    setBusy(changeId);
    try {
      onRestored(await usersApi.daUndo(id, changeId));
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(null);
    }
  };
  if (q.isPending) return null;
  if (q.isError) return <ErrorView error={q.error} />;
  const list = more ? q.data : q.data.slice(0, 3);
  const when = (at: string) => new Date(at).toLocaleString('he-IL', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex flex-col gap-2" data-testid="da-history">
      <h4 className="m-0 inline-flex items-center gap-1.5 text-sm font-semibold">
        <IconHistory size={16} />
        {D.history}
      </h4>
      {q.data.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">{D.historyNone}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col divide-y divide-border overflow-hidden rounded-md border border-border bg-card p-0">
          {list.map((e) => (
            <li key={e.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2" data-testid="da-history-row">
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm" data-testid="da-history-text">
                  {e.textHe}
                </span>
                <span className="text-[12px] text-muted-foreground">
                  {e.actorNameHe} · {when(e.at)}
                </span>
              </span>
              {e.canUndo && (
                <Button size="sm" variant="outline" disabled={busy != null} onClick={() => void undo(e.id)} data-testid="da-undo">
                  {busy === e.id ? D.undoing : D.undo}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!more && q.data.length > 3 && (
        <Button variant="ghost" size="sm" className="w-fit" onClick={() => setMore(true)}>
          {D.historyMore(q.data.length - 3)}
        </Button>
      )}
      {err != null && <ErrorView error={err} />}
    </div>
  );
}

/** פס דק ליד "נכון ל-": משתמש מוגבל רואה רק את מה שהוקצה לו (GET /api/users/me → dataAccess.mode). */
export function RestrictedNote() {
  const me = useQuery({ queryKey: ['users-me'], queryFn: usersApi.me, staleTime: 60_000, retry: false });
  if (me.data?.dataAccess?.mode !== 'restricted') return null;
  return (
    <p className="m-0 inline-flex items-center gap-1.5 text-[13px] text-muted-foreground" data-testid="data-restricted">
      <IconLock size={13} />
      {D.banner}
    </p>
  );
}
